import { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookingOrigin, Price, PriceStatus } from '@utriper/core';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { usePriceGroupTab } from 'core/contexts/priceGroupTab';
import { PreviewService } from 'core/api/services/preview';
import Layout from 'components/Layout';
import { FORM_IDS } from './styles';
import { Section } from '../styles';
import { Box, Button, Fade, Grid2, IconButton, InputLabel, styled, Typography, useMediaQuery } from '@mui/material';
import { CircularIndeterminate, Chip, PreviewExperience, AlertDialog, Alert } from '@utriper/core-ui';
import { Add, DriveFileMove, Edit } from '@mui/icons-material';
import PriceGroupForm from './components/PriceGroupForm';
import AddUnitForm from './components/AddUnitForm';
import AddRangeForm from './components/AddRangeForm';
import AddOriginForm from './components/AddOriginForm';
import EditUnitForm from './components/EditUnitForm';
import EditRangesForm from './components/EditRangesForm';
import DeleteUnit from './components/DeleteUnit';
import DeleteRange from './components/DeleteRange';
import PriceGroupGrid from '../components/PriceGroupGrid';
import DeleteOrigin from './components/DeleteOrigin';
import ChangeListForm from './components/ChangeListForm';
import OtaPriceActivationAlert from '../components/OtaPriceActivationAlert';

const Container = ({ children, ...props }) => {
    return (
        <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" {...props}>
            {children}
        </Grid2>
    );
};

const Label = styled(InputLabel)({
    fontSize: '16px',
    fontWeight: 400,
    color: '#131313bf',
});

const PriceGroupIdPage = () => {
    const { id } = useParams();
    const priceGroupId = parseInt(id);
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`price_group_id.${key}`);
    const { getUnitsData, getPriceGroupById, handleUpdatePrice, loadingPriceGroup, loadingPrices, priceGroup, prices, setPrices, unitsPriceGroup, bookingOrigins, units } = usePriceGroup();
    const { getStatusColor } = usePriceGroupTab();
    const history = useHistory();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const [openForm, setOpenForm] = useState(false);
    const [formId, setFormId] = useState(0);
    const [unitId, setUnitId] = useState(0);
    const [unitToDelete, setUnitToDelete] = useState(null);
    const [rangeToDelete, setRangeToDelete] = useState(null);
    const [originToDelete, setOriginToDelete] = useState(null);
    const [newPrice, setNewPrice] = useState(null);

    const canAddUnit = useMemo(() => {
        const existingTypeIds = new Set(unitsPriceGroup.map((u) => u.unitType));
        return units.some((u) => !existingTypeIds.has(u.typeId));
    }, [unitsPriceGroup, units]);

    const allOrigins = BookingOrigin.getValidOrigins();
    const canAddBookingOrigin = useMemo(() => {
        const existingOrigins = new Set(bookingOrigins.map((origin) => origin));
        return allOrigins.some((origin) => !existingOrigins.has(origin));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingOrigins]);

    useEffect(() => {
        getUnitsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getPriceGroupById(priceGroupId, history);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceGroupId]);

    useEffect(() => {
        if (newPrice) {
            handleUpdatePrice(priceGroupId, newPrice);
            setNewPrice(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newPrice]);

    const priceType = Number.isInteger(priceGroup?.type) ? Price.getTranslatedName(Price.types, priceGroup.type, i18n.language) : '';
    const statusLabel = Number.isInteger(priceGroup?.status) ? PriceStatus.getTranslatedName(PriceStatus.status, priceGroup?.status, i18n.language) : '';
    const statusColor = getStatusColor(priceGroup?.status);

    const handleClose = () => {
        setOpenForm(false);
        setFormId(0);
        setUnitId(0);
        setUnitToDelete(null);
        setRangeToDelete(null);
        setOriginToDelete(null);
    };

    const handleOpen = () => setOpenForm(true);

    const handleOpenForm = (formId, item) => {
        if (formId === FORM_IDS.EDIT_UNIT && item?.unitId) {
            setUnitId(item.unitId);
        }

        if (formId === FORM_IDS.DELETE_UNIT && item?.unitId) {
            setUnitToDelete(item);
        }

        if (formId === FORM_IDS.DELETE_RANGE && item?.rowId) {
            setRangeToDelete(item);
        }

        if (formId === FORM_IDS.DELETE_ORIGIN && item?.bookingOriginId) {
            setOriginToDelete(item.bookingOriginId);
        }

        setFormId(formId);
        handleOpen();
    };

    const RenderForm = () => {
        const formProps = { priceGroupId, handleClose };

        switch (formId) {
            case FORM_IDS.EDIT_PRICE_GROUP_DATA:
                return <PriceGroupForm {...formProps} />;
            case FORM_IDS.ADD_UNIT:
                return <AddUnitForm {...formProps} />;
            case FORM_IDS.ADD_RANGE:
                return <AddRangeForm {...formProps} />;
            case FORM_IDS.ADD_ORIGIN:
                return <AddOriginForm {...formProps} />;
            case FORM_IDS.EDIT_UNIT:
                return <EditUnitForm {...formProps} unitId={unitId} />;
            case FORM_IDS.EDIT_RANGES:
                return <EditRangesForm {...formProps} />;
            case FORM_IDS.DELETE_UNIT:
                return <DeleteUnit {...formProps} unitToDelete={unitToDelete} />;
            case FORM_IDS.DELETE_RANGE:
                return <DeleteRange {...formProps} rangeToDelete={rangeToDelete} />;
            case FORM_IDS.DELETE_ORIGIN:
                return <DeleteOrigin {...formProps} bookingOriginId={originToDelete} />;
            case FORM_IDS.CHANGE_LIST:
                return <ChangeListForm {...formProps} />;
            default:
                break;
        }
    };

    return (
        <Layout>
            {loadingPriceGroup ? (
                <CircularIndeterminate centered />
            ) : (
                <Fade in={!loadingPriceGroup}>
                    <Section>
                        {/* Title */}
                        <Grid2 container justifyContent="space-between" alignItems="center" size={12}>
                            <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" size={10}>
                                <Grid2 container justifyContent="flex-start" alignItems="center" gap="8px" size={12} flexWrap="nowrap">
                                    <Typography fontSize={20} fontWeight={900} sx={{ color: '#131313' }} noWrap>
                                        {priceGroup?.name}
                                    </Typography>
                                    <Chip label={statusLabel} sx={{ background: `${statusColor}40`, color: `${statusColor}`, padding: '8px 4px', borderRadius: '4px' }} />
                                </Grid2>
                                {priceGroup?.description && (
                                    <Grid2 container justifyContent="flex-start" alignItems="center" gap="8px" size={12}>
                                        <Typography fontSize={16} fontWeight={500} sx={{ color: '#131313bf' }} noWrap>
                                            {priceGroup.description}
                                        </Typography>
                                    </Grid2>
                                )}
                            </Grid2>
                            <Grid2 container justifyContent="flex-end" alignItems="center" size={2}>
                                {isMobile ? (
                                    <IconButton onClick={() => handleOpenForm(FORM_IDS.EDIT_PRICE_GROUP_DATA)}>
                                        <Edit sx={{ color: '#131313' }} />
                                    </IconButton>
                                ) : (
                                    <Button
                                        sx={{
                                            padding: '8px 14px',
                                            backgroundColor: '#131313',
                                            fontWeight: 600,
                                            width: { xs: '100%', md: 'fit-content' },
                                            textTransform: 'none',
                                        }}
                                        variant="contained"
                                        startIcon={<Edit />}
                                        onClick={() => handleOpenForm(FORM_IDS.EDIT_PRICE_GROUP_DATA)}
                                    >
                                        {translate('edit_button')}
                                    </Button>
                                )}
                            </Grid2>
                        </Grid2>

                        {/* Data */}
                        <Grid2 size={12} container direction="column" mt="32px" gap="16px">
                            <Container size={12}>
                                <Label>{translate('price_group_tab_label')}</Label>
                                <Typography fontSize={18} fontWeight={600} sx={{ color: '#131313' }}>
                                    {priceGroup?.priceGroupTabName}
                                </Typography>
                            </Container>

                            <Container
                                size={12}
                                sx={{
                                    '& span': {
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: '#131313',
                                    },
                                }}
                            >
                                <Label>{translate('experience_label')}</Label>
                                <PreviewExperience id={priceGroup?.experienceId} previewService={PreviewService} label="name" />
                            </Container>

                            <Grid2 size={12} container justifyContent="flex-start" alignItems="flex-start">
                                <Container size={6}>
                                    <Label>{translate('currency_label')}</Label>
                                    <Typography fontSize={18} fontWeight={600} sx={{ color: '#131313' }}>
                                        {priceGroup?.currency}
                                    </Typography>
                                </Container>
                                <Container size={6}>
                                    <Label>{translate('price_type_label')}</Label>
                                    <Typography fontSize={18} fontWeight={600} sx={{ color: '#131313' }}>
                                        {priceType}
                                    </Typography>
                                </Container>
                            </Grid2>

                            {/* Grid */}
                            <PriceGroupGrid
                                data={{
                                    units: unitsPriceGroup,
                                    bookingOrigins,
                                    prices,
                                }}
                                actions={{
                                    handleUpdatePrice: setNewPrice,
                                    handleOpenEdit: handleOpenForm,
                                    handleOpenDelete: handleOpenForm,
                                    setPrices,
                                }}
                                config={{
                                    canEdit: true,
                                }}
                                ui={{
                                    loading: loadingPrices,
                                }}
                            />

                            {/* Add Buttons */}
                            <Box display="flex" flexDirection="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                {canAddUnit && (
                                    <Button sx={{ color: 'var(--color-utriper-blue)', textTransform: 'none' }} variant="text" startIcon={<Add />} onClick={() => handleOpenForm(FORM_IDS.ADD_UNIT)}>
                                        {translate('add_unit')}
                                    </Button>
                                )}

                                {canAddBookingOrigin && (
                                    <Button
                                        sx={{
                                            color: 'var(--color-utriper-blue)',
                                            textTransform: 'none',
                                        }}
                                        variant="text"
                                        startIcon={<Add />}
                                        onClick={() => handleOpenForm(FORM_IDS.ADD_ORIGIN)}
                                    >
                                        {translate('add_origin')}
                                    </Button>
                                )}

                                <Button sx={{ color: 'var(--color-utriper-blue)', textTransform: 'none' }} variant="text" startIcon={<Add />} onClick={() => handleOpenForm(FORM_IDS.ADD_RANGE)}>
                                    {translate('add_range')}
                                </Button>
                            </Box>

                            {/* ALERT */}
                            <Alert collapsible severity="info" title={translate('how_it_works_alert.title')}>
                                <Typography>{translate('how_it_works_alert.subtitle')}</Typography>

                                <Typography sx={{ mt: 1 }}>{translate('how_it_works_alert.intro')}</Typography>

                                <Typography sx={{ mt: 1 }}>{translate('how_it_works_alert.actions_title')}</Typography>

                                <Box component="ul" sx={{ pl: '2em', margin: 0, mt: 1, '& li': { mb: 0.5 } }}>
                                    <li>{translate('how_it_works_alert.units')}</li>

                                    <li>{translate('how_it_works_alert.booking_origin')}</li>

                                    <li>{translate('how_it_works_alert.quantity_range')}</li>
                                </Box>
                                <Typography sx={{ mt: 1.5 }}>{translate('how_it_works_alert.delete')}</Typography>
                            </Alert>

                            <OtaPriceActivationAlert bookingOrigins={bookingOrigins} />

                            <Button
                                sx={{
                                    backgroundColor: 'var(--color-utriper-blue)',
                                    color: 'white',
                                    textTransform: 'none',
                                    width: 'fit-content',
                                }}
                                variant="contained"
                                startIcon={<DriveFileMove />}
                                onClick={() => handleOpenForm(FORM_IDS.CHANGE_LIST)}
                            >
                                {translate('change_list_button')}
                            </Button>
                        </Grid2>
                    </Section>
                </Fade>
            )}

            <AlertDialog open={openForm} handleClose={handleClose} hideTitle hideActions maxWidth="sm" fullWidth dialogContent={<RenderForm />} />
        </Layout>
    );
};

export default PriceGroupIdPage;
